// ctl-inbox.js
// <ctl-inbox> — conversation inbox list
// - No dependencies
// - Slot templates: <template slot="item"> with {{placeholders}}
// - Slots: toolbar, item(template), empty, error
// - Props: items (array), filters (object)
// - Attrs: value, loading, error, dense
// - Events: ctl-select, ctl-search, ctl-filter, ctl-item-action
//
// Template placeholders for slot="item":
// {{id}}, {{title}}, {{preview}}, {{updatedAt}}, {{updatedAtText}}
// {{unreadCount}}, {{status}}, {{primaryChannel}}, {{persona}}
// {{escalationLevel}}, {{assignedToName}}, {{tags}}

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-radius: var(--ctl-radius-lg, 14px);
    --ctl-shadow: var(--ctl-shadow-sm, 0 6px 18px rgba(0,0,0,.10));
    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .frame{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius);
    box-shadow: var(--ctl-shadow);
    overflow: hidden;
  }

  .toolbar{
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
  }
  .toolbar:empty{ display:none; }

  .body{
    position: relative;
    background: var(--ctl-color-surface);
  }

  .list{
    list-style:none;
    padding: 0;
    margin: 0;
  }

  .row{
    display:flex;
    gap: var(--ctl-space-2-5);
    align-items:flex-start;
    padding: var(--ctl-space-3) var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    cursor: pointer;
    outline: none;
  }
  :host([dense]) .row{ padding: var(--ctl-space-2) var(--ctl-space-3); }
  .row:last-child{ border-bottom: 0; }

  .row:hover{
    background: color-mix(in srgb, var(--ctl-color-brand) 4%, var(--ctl-color-surface));
  }

  .row[aria-selected="true"]{
    background: color-mix(in srgb, var(--ctl-color-brand) 8%, var(--ctl-color-surface));
  }

  .left{
    flex: 1 1 auto;
    min-width: 0;
  }

  .topline{
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
  }

  .title{
    font-weight: var(--ctl-font-weight-ultra);
    font-size: var(--ctl-font-size-md);
    letter-spacing: .2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-1-5);
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-extrabold);
    white-space: nowrap;
  }

  .pill{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
    padding: var(--ctl-space-0-5) 7px;
    border-radius: var(--ctl-radius-full);
    font-size: var(--ctl-font-size-2xs);
    font-weight: var(--ctl-font-weight-black);
    letter-spacing:.2px;
  }

  .preview{
    margin-top: var(--ctl-space-1);
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-strong);
    font-size: var(--ctl-font-size-sm);
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .right{
    display:flex;
    flex-direction: column;
    align-items:flex-end;
    gap: var(--ctl-space-1-5);
    flex: 0 0 auto;
  }

  .unread{
    min-width: 18px;
    height: 18px;
    border-radius: var(--ctl-radius-full);
    background: var(--ctl-color-brand);
    color: var(--ctl-color-text-inverse);
    display:grid;
    place-items:center;
    font-weight: var(--ctl-font-weight-ultra);
    font-size: var(--ctl-font-size-xs);
    padding: 0 var(--ctl-space-1-5);
  }

  .actions{
    display:flex;
    gap: var(--ctl-space-1-5);
  }

  .iconBtn{
    all: unset;
    cursor: pointer;
    border-radius: var(--ctl-radius-10);
    padding: var(--ctl-space-1-5) var(--ctl-space-2);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-black);
    color: var(--ctl-color-muted);
  }
  .iconBtn:hover{ filter: brightness(.98); }
  .iconBtn:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }

  .row:focus-visible{
    outline: none;
    box-shadow: inset var(--ctl-focus-ring);
  }

  /* Overlays */
  .overlay{
    position:absolute;
    inset: 0;
    display:none;
    align-items:center;
    justify-content:center;
    background: color-mix(in srgb, var(--ctl-color-surface) 70%, transparent);
    z-index: 2;
    padding: var(--ctl-space-3-5);
  }
  :host([loading]) .overlay.loading{ display:flex; }
  :host([error]) .overlay.error{ display:flex; background: color-mix(in srgb, var(--ctl-color-surface) 80%, transparent); }

  .overlayCard{
    width: min(520px, 100%);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius-14);
    padding: var(--ctl-space-3);
    box-shadow: var(--ctl-shadow);
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-bold);
    font-size: var(--ctl-font-size-md);
    text-align:center;
  }

  .empty{
    padding: var(--ctl-space-3-5);
    text-align:center;
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-bold);
    font-size: var(--ctl-font-size-md);
  }

  /* Hide light-dom templates used for render hook */
  ::slotted(template[slot="item"]){
    display:none;
  }
</style>

<div class="frame" part="frame">
  <div class="toolbar" part="toolbar"><slot name="toolbar"></slot></div>

  <div class="body" part="body">
    <div class="overlay loading" part="loading" aria-hidden="true">
      <div class="overlayCard">
        <div style="font-weight:var(--ctl-font-weight-black); color: var(--ctl-color-text); margin-bottom: var(--ctl-space-1-5);">Loading…</div>
        <div>Fetching conversations.</div>
      </div>
    </div>

    <div class="overlay error" part="error" aria-hidden="true">
      <div class="overlayCard"><slot name="error">Unable to load inbox.</slot></div>
    </div>

    <ul class="list" id="list" role="listbox" aria-label="Inbox"></ul>

    <div class="empty" id="empty" hidden>
      <slot name="empty">No conversations.</slot>
    </div>

    <slot id="itemTemplateSlot" name="item"></slot>
  </div>
</div>
`;

function toArray(v) { return Array.isArray(v) ? v : []; }
function isObj(v) { return v && typeof v === "object"; }

function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

function fmtUpdated(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

function normalizeConversation(c) {
  const it = isObj(c) ? c : {};
  const id = String(it.id ?? "");
  const title = String(it.title ?? it.customerName ?? it.subject ?? "Conversation");
  const preview = String(it.preview ?? "");
  const updatedAt = String(it.updatedAt ?? it.lastMessageAt ?? "");
  const unreadCount = Number(it.unreadCount ?? 0);
  const status = String(it.status ?? "open");
  const primaryChannel = String(it.primaryChannel ?? it.channel ?? "");
  const persona = String(it.persona ?? "");
  const escalationLevel = Number(it.escalationLevel ?? it.level ?? 1);
  const assignedTo = isObj(it.assignedTo) ? it.assignedTo : null;
  const assignedToName = assignedTo ? String(assignedTo.name ?? "") : "";
  const tags = Array.isArray(it.tags) ? it.tags.map((t) => String(t)) : [];

  return {
    ...it,
    id,
    title,
    preview,
    updatedAt,
    updatedAtText: fmtUpdated(updatedAt),
    unreadCount: Number.isFinite(unreadCount) ? Math.max(0, unreadCount) : 0,
    status,
    primaryChannel,
    persona,
    escalationLevel: Number.isFinite(escalationLevel) ? escalationLevel : 1,
    assignedTo,
    assignedToName,
    tags
  };
}

export class CtlInbox extends HTMLElement {
  static get observedAttributes() {
    return ["value", "loading", "error", "dense", "items", "filters"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._list = this.shadowRoot.getElementById("list");
    this._empty = this.shadowRoot.getElementById("empty");
    this._tmplSlot = this.shadowRoot.getElementById("itemTemplateSlot");

    this._items = [];
    this._filters = null;
    this._itemTemplate = null;

    this._onSlotChange = this._onSlotChange.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    // JSON attr hydration (optional)
    if (this.hasAttribute("items")) this.items = safeJsonParse(this.getAttribute("items"), []);
    if (this.hasAttribute("filters")) this.filters = safeJsonParse(this.getAttribute("filters"), null);

    this._tmplSlot.addEventListener("slotchange", this._onSlotChange);
    this.shadowRoot.addEventListener("click", this._onClick);
    this.shadowRoot.addEventListener("keydown", this._onKeyDown);

    this._collectTemplate();
    this._render();
    this._sync();
  }

  disconnectedCallback() {
    this._tmplSlot.removeEventListener("slotchange", this._onSlotChange);
    this.shadowRoot.removeEventListener("click", this._onClick);
    this.shadowRoot.removeEventListener("keydown", this._onKeyDown);
  }

  attributeChangedCallback(name) {
    if (name === "items") this.items = safeJsonParse(this.getAttribute("items"), []);
    if (name === "filters") this.filters = safeJsonParse(this.getAttribute("filters"), null);
    if (name === "value" || name === "loading" || name === "error" || name === "dense") {
      this._render();
      this._sync();
    }
  }

  // ---- attrs ----
  get value() { return this.getAttribute("value"); }
  set value(v) { v == null ? this.removeAttribute("value") : this.setAttribute("value", String(v)); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get error() { return this.hasAttribute("error"); }
  set error(v) { v ? this.setAttribute("error", "") : this.removeAttribute("error"); }

  get dense() { return this.hasAttribute("dense"); }
  set dense(v) { v ? this.setAttribute("dense", "") : this.removeAttribute("dense"); }

  // ---- props ----
  get items() { return this._items; }
  set items(v) {
    this._items = toArray(v).map(normalizeConversation).filter((x) => x.id);
    this.setAttribute("items", JSON.stringify(this._items));
    this._render();
    this._sync();
  }

  get filters() { return this._filters; }
  set filters(v) {
    this._filters = isObj(v) ? v : null;
    this.setAttribute("filters", JSON.stringify(this._filters));
  }

  // ---- template override ----
  _onSlotChange() {
    this._collectTemplate();
    this._render();
  }

  _collectTemplate() {
    const assigned = this._tmplSlot.assignedElements({ flatten: true }) || [];
    this._itemTemplate = assigned.find((el) => el.tagName === "TEMPLATE") || null;
  }

  _sync() {
    const showEmpty = this._items.length === 0 && !this.loading && !this.error;
    this._empty.hidden = !showEmpty;
  }

  _render() {
    const items = this._items;
    const selectedId = this.value;

    if (items.length === 0) {
      this._list.innerHTML = "";
      return;
    }

    const html = items.map((it) => {
      const selected = selectedId && String(selectedId) === it.id;

      let inner = "";
      if (this._itemTemplate) {
        inner = this._renderTemplate(this._itemTemplate, it);
      } else {
        const channel = it.primaryChannel ? it.primaryChannel.toUpperCase() : "";
        const persona = it.persona ? it.persona.toUpperCase() : "";
        const level = it.escalationLevel ? `L${it.escalationLevel}` : "";
        inner = `
          <div class="left">
            <div class="topline">
              <div class="title">${escapeHtml(it.title)}</div>
              <div class="meta">
                ${channel ? `<span class="pill">${escapeHtml(channel)}</span>` : ""}
                ${persona ? `<span class="pill">${escapeHtml(persona)}</span>` : ""}
                ${level ? `<span class="pill">${escapeHtml(level)}</span>` : ""}
                ${it.updatedAtText ? `<span>${escapeHtml(it.updatedAtText)}</span>` : ""}
              </div>
            </div>
            ${it.preview ? `<div class="preview">${escapeHtml(it.preview)}</div>` : ""}
          </div>

          <div class="right">
            ${it.unreadCount > 0 ? `<div class="unread" aria-label="${escapeHtml(it.unreadCount)} unread">${escapeHtml(it.unreadCount)}</div>` : `<div style="height:18px;"></div>`}
            <div class="actions">
              <button class="iconBtn" type="button" data-role="item-action" data-action="pin" data-id="${escapeHtml(it.id)}" aria-label="Pin">Pin</button>
              <button class="iconBtn" type="button" data-role="item-action" data-action="more" data-id="${escapeHtml(it.id)}" aria-label="More">⋯</button>
            </div>
          </div>
        `;
      }

      return `
        <li
          class="row"
          role="option"
          tabindex="${selected ? "0" : "-1"}"
          aria-selected="${selected ? "true" : "false"}"
          data-role="row"
          data-id="${escapeHtml(it.id)}"
        >${inner}</li>
      `;
    }).join("");

    this._list.innerHTML = html;
  }

  _renderTemplate(tmpl, data) {
    const html = tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      if (Array.isArray(v)) return escapeHtml(v.join(", "));
      return escapeHtml(v == null ? "" : String(v));
    });
    return html;
  }

  // ---- Interaction ----
  _onClick(e) {
    const path = e.composedPath ? e.composedPath() : [];

    // item action
    const actionEl = this._findInPath(path, (el) => el?.dataset?.role === "item-action");
    if (actionEl) {
      e.preventDefault();
      e.stopPropagation();
      const id = String(actionEl.dataset.id || "");
      const action = String(actionEl.dataset.action || "action");
      const item = this._items.find((x) => x.id === id) || null;

      this.dispatchEvent(new CustomEvent("ctl-item-action", {
        bubbles: true,
        composed: true,
        detail: { id, action, item }
      }));
      return;
    }

    // row select
    const row = this._findInPath(path, (el) => el?.dataset?.role === "row" && el?.dataset?.id);
    if (row) {
      const id = String(row.dataset.id);
      this._selectById(id);
    }
  }

  _onKeyDown(e) {
    // roving tabindex within listbox
    const key = e.key;
    if (key !== "ArrowDown" && key !== "ArrowUp" && key !== "Home" && key !== "End" && key !== "Enter" && key !== " ") return;

    const rows = Array.from(this.shadowRoot.querySelectorAll('[data-role="row"]'));
    if (rows.length === 0) return;

    const active = this.shadowRoot.activeElement;
    const currentIndex = Math.max(0, rows.findIndex((r) => r === active));

    let nextIndex = currentIndex;

    if (key === "ArrowDown") nextIndex = Math.min(rows.length - 1, currentIndex + 1);
    if (key === "ArrowUp") nextIndex = Math.max(0, currentIndex - 1);
    if (key === "Home") nextIndex = 0;
    if (key === "End") nextIndex = rows.length - 1;

    if (key === "Enter" || key === " ") {
      e.preventDefault();
      const row = rows[currentIndex];
      if (row) this._selectById(String(row.dataset.id || ""));
      return;
    }

    if (nextIndex !== currentIndex) {
      e.preventDefault();
      rows.forEach((r, i) => r.setAttribute("tabindex", i === nextIndex ? "0" : "-1"));
      rows[nextIndex].focus();
    }
  }

  _selectById(id) {
    const item = this._items.find((x) => x.id === id) || null;
    this.value = id;

    // update aria-selected and tabindex without full rerender if possible
    const rows = Array.from(this.shadowRoot.querySelectorAll('[data-role="row"]'));
    for (const r of rows) {
      const isSel = String(r.dataset.id) === String(id);
      r.setAttribute("aria-selected", isSel ? "true" : "false");
      r.setAttribute("tabindex", isSel ? "0" : "-1");
    }

    this.dispatchEvent(new CustomEvent("ctl-select", {
      bubbles: true,
      composed: true,
      detail: { id, item }
    }));
  }

  _findInPath(path, predicate) {
    for (const el of path) {
      if (predicate(el)) return el;
    }
    return null;
  }
}

customElements.define("ctl-inbox", CtlInbox);
